import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'

import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'
import { dateFnsLocale } from '@/utils/dateLocale'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import {
  yandexMapsLoadFailed,
  loadYandexMaps,
} from '@/lib/yandexMapsLoader'
import { YANDEX_MAPS_API_KEY } from '@/config'
import { useInspectorTrack, type TrackPoint } from '@/hooks/useInspectorTrack'

interface ProfileLite {
  id: string
  full_name: string | null
  role: string | null
  last_lat: number | null
  last_lon: number | null
  last_position_at: string | null
}

const MOSCOW_CENTER: [number, number] = [55.751244, 37.618423]

type RangePreset = '1h' | '24h' | '7d' | 'custom'

interface RangeState {
  preset: RangePreset
  from: string // ISO
  to: string // ISO
}

function presetRange(p: Exclude<RangePreset, 'custom'>): { from: string; to: string } {
  const now = new Date()
  const ms =
    p === '1h'
      ? 60 * 60 * 1000
      : p === '24h'
        ? 24 * 60 * 60 * 1000
        : 7 * 24 * 60 * 60 * 1000
  return { from: new Date(now.getTime() - ms).toISOString(), to: now.toISOString() }
}

/**
 * Track viewer инспектора. Шапка — текущая позиция (`profiles.last_*`),
 * ниже — Yandex Map с polyline за выбранный период (1ч / 24ч / 7д /
 * произвольно). Realtime: smart-hybrid через `useInspectorTrack`.
 *
 * Маршрут: `/inspectors/:id/last-position`. Доступ — admin/dispatcher
 * (RLS на `inspector_position_history` + политики `iph_select_*`).
 */
export function InspectorLastPositionPage() {
  const { t } = useTranslation()
  const { id: userId } = useParams<{ id: string }>()
  const supabase = createClient()

  // ---------------------------------------------------------------------------
  // Шапка: профиль + текущая позиция
  // ---------------------------------------------------------------------------
  const { data: profile, isLoading: profileLoading, error: profileError } =
    useQuery({
      queryKey: ['inspector-profile-lite', userId],
      enabled: Boolean(userId),
      refetchInterval: 30_000,
      queryFn: async () => {
        if (!userId) return null
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name, role, last_lat, last_lon, last_position_at')
          .eq('id', userId)
          .maybeSingle()
        if (error) throw error
        return data as ProfileLite | null
      },
    })

  // ---------------------------------------------------------------------------
  // Period picker
  // ---------------------------------------------------------------------------
  const [range, setRange] = useState<RangeState>(() => {
    const r = presetRange('24h')
    return { preset: '24h', ...r }
  })

  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  function applyPreset(preset: Exclude<RangePreset, 'custom'>) {
    const r = presetRange(preset)
    setRange({ preset, ...r })
  }

  function applyCustom() {
    if (!customFrom || !customTo) return
    const fromIso = new Date(customFrom).toISOString()
    const toIso = new Date(customTo).toISOString()
    setRange({ preset: 'custom', from: fromIso, to: toIso })
  }

  // ---------------------------------------------------------------------------
  // Загрузка трека
  // ---------------------------------------------------------------------------
  const trackQ = useInspectorTrack({
    inspectorId: userId,
    from: range.from,
    to: range.to,
  })

  const points: TrackPoint[] = trackQ.data ?? []

  // ---------------------------------------------------------------------------
  // Карта (Yandex)
  // ---------------------------------------------------------------------------
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<unknown>(null)
  const layerRef = useRef<unknown[]>([])
  const [mapReady, setMapReady] = useState(false)
  const [mapLoadError, setMapLoadError] = useState<string | null>(null)

  // Bootstrap карты — один раз.
  useLayoutEffect(() => {
    if (!YANDEX_MAPS_API_KEY) return
    const el = mapContainerRef.current
    if (!el) return
    let cancelled = false

    void (async () => {
      try {
        await loadYandexMaps()
      } catch (e) {
        if (!cancelled) {
          setMapLoadError(
            e instanceof Error ? e.message : yandexMapsLoadFailed(),
          )
        }
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ymaps = (window as any).ymaps
      if (cancelled || !ymaps || mapRef.current) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const map = new (ymaps as any).Map(
        el,
        {
          center: MOSCOW_CENTER,
          zoom: 10,
          controls: ['zoomControl', 'fullscreenControl'],
        },
        { suppressMapOpenBlock: true },
      )
      mapRef.current = map
      setMapReady(true)
    })()

    return () => {
      cancelled = true
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(mapRef.current as any)?.destroy?.()
      } catch {
        /* empty */
      }
      mapRef.current = null
      layerRef.current = []
    }
  }, [])

  // Перерисовка трека при изменении points / mapReady.
  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ymaps = (window as unknown as any).ymaps
    if (!map || !ymaps) return

    // Чистим старый слой.
    for (const obj of layerRef.current) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(map as any).geoObjects.remove(obj)
      } catch {
        /* empty */
      }
    }
    layerRef.current = []

    if (points.length === 0) return

    // Разбиваем на сегменты по mode (idle/active_order) для цветовой
    // индикации; даём каждому свой цвет.
    let segStart = 0
    for (let i = 1; i <= points.length; i++) {
      const isLast = i === points.length
      const modeChanged =
        !isLast && points[i].mode !== points[segStart].mode
      if (isLast || modeChanged) {
        const seg = points.slice(segStart, i)
        if (seg.length >= 2) {
          const coords = seg.map(
            (p) => [p.lat, p.lon] as [number, number],
          )
          const isActive = seg[0].mode === 'active_order'
          const line = new ymaps.Polyline(
            coords,
            {},
            {
              strokeColor: isActive ? '#F7931A' : '#94A3B8',
              strokeWidth: isActive ? 4 : 2,
              strokeStyle: 'solid',
              interactivityModel: 'default#transparent',
            },
          )
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(map as any).geoObjects.add(line)
          layerRef.current.push(line)
        }
        segStart = i
      }
    }

    // Маркер начала и конца.
    const first = points[0]
    const last = points[points.length - 1]
    const startPm = new ymaps.Placemark(
      [first.lat, first.lon],
      {
        balloonContent: i18n.t('geo.track.start', {
          time: new Date(first.recorded_at).toLocaleString('ru-RU'),
        }),
      },
      { preset: 'islands#greenCircleDotIcon' },
    )
    const endPm = new ymaps.Placemark(
      [last.lat, last.lon],
      {
        balloonContent: i18n.t('geo.track.finish', {
          time: new Date(last.recorded_at).toLocaleString('ru-RU'),
          ago: formatDistanceToNow(new Date(last.recorded_at), {
            locale: dateFnsLocale(),
            addSuffix: true,
          }),
        }),
      },
      { preset: 'islands#redCircleDotIcon' },
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(map as any).geoObjects.add(startPm)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(map as any).geoObjects.add(endPm)
    layerRef.current.push(startPm, endPm)

    // Авто-zoom на bounds.
    const lats = points.map((p) => p.lat)
    const lons = points.map((p) => p.lon)
    const bounds: [[number, number], [number, number]] = [
      [Math.min(...lats), Math.min(...lons)],
      [Math.max(...lats), Math.max(...lons)],
    ]
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(map as any).setBounds(bounds, { checkZoomRange: true, zoomMargin: 40 })
    } catch {
      /* empty */
    }
  }, [points, mapReady])

  // ---------------------------------------------------------------------------
  // Вычисляем суммарную статистику для шапки
  // ---------------------------------------------------------------------------
  const stats = useMemo(() => {
    if (points.length === 0) {
      return { total: 0, idle: 0, active: 0, distanceKm: 0 }
    }
    let idle = 0
    let active = 0
    for (const p of points) {
      if (p.mode === 'active_order') active++
      else idle++
    }
    let dist = 0
    for (let i = 1; i < points.length; i++) {
      dist += haversineKm(
        { lat: points[i - 1].lat, lon: points[i - 1].lon },
        { lat: points[i].lat, lon: points[i].lon },
      )
    }
    return { total: points.length, idle, active, distanceKm: dist }
  }, [points])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div>
      <PageHeader
        title={
          profile?.full_name
            ? t('geo.track.title', { name: profile.full_name })
            : t('geo.track.titleNoName')
        }
        subtitle={userId}
        actions={
          <button
            type="button"
            onClick={() => trackQ.refetch()}
            disabled={trackQ.isFetching}
            className="rounded border border-[color:var(--stryzh-color-orange)]/50 px-3 py-1.5 text-sm text-[color:var(--stryzh-color-orange)] disabled:opacity-50"
          >
            {trackQ.isFetching ? t('geo.track.updating') : t('geo.track.refresh')}
          </button>
        }
      />

      {/* Текущая позиция (шапка) */}
      {profileLoading ? (
        <Loader />
      ) : profileError ? (
        <Banner
          tone="error"
          text={t('geo.positions.errorPrefix', {
            message: (profileError as Error).message,
          })}
        />
      ) : !profile?.last_position_at ? (
        <Banner tone="muted" text={t('geo.track.noPositions')} />
      ) : (
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field
            label={t('geo.track.currentPosition')}
            value={
              profile.last_lat != null && profile.last_lon != null
                ? `${profile.last_lat.toFixed(6)}, ${profile.last_lon.toFixed(6)}`
                : '—'
            }
            mono
          />
          <Field
            label={t('geo.track.recorded')}
            value={`${new Date(profile.last_position_at).toLocaleString('ru-RU')} (${formatDistanceToNow(new Date(profile.last_position_at), { locale: dateFnsLocale(), addSuffix: true })})`}
          />
          <Field label={t('geo.track.pointsForPeriod')} value={String(stats.total)} mono />
        </div>
      )}

      {/* Period picker */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(['1h', '24h', '7d'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => applyPreset(p)}
            className={
              'rounded border px-3 py-1.5 text-sm ' +
              (range.preset === p
                ? 'border-[color:var(--stryzh-color-orange)] bg-[color:var(--stryzh-color-orange)]/10 text-[color:var(--stryzh-color-orange)]'
                : 'border-white/15 text-[color:var(--stryzh-color-text-secondary)]')
            }
          >
            {p === '1h'
              ? t('geo.track.preset1h')
              : p === '24h'
                ? t('geo.track.preset24h')
                : t('geo.track.preset7d')}
          </button>
        ))}
        <span className="mx-2 text-[color:var(--stryzh-color-text-secondary)]">|</span>
        <input
          type="datetime-local"
          value={customFrom}
          onChange={(e) => setCustomFrom(e.target.value)}
          className="rounded border border-white/15 bg-transparent px-2 py-1 text-sm text-[color:var(--stryzh-color-text-primary)]"
        />
        <span className="text-[color:var(--stryzh-color-text-secondary)]">—</span>
        <input
          type="datetime-local"
          value={customTo}
          onChange={(e) => setCustomTo(e.target.value)}
          className="rounded border border-white/15 bg-transparent px-2 py-1 text-sm text-[color:var(--stryzh-color-text-primary)]"
        />
        <button
          type="button"
          onClick={applyCustom}
          disabled={!customFrom || !customTo}
          className="rounded border border-white/15 px-3 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)] disabled:opacity-50"
        >
          {t('geo.track.apply')}
        </button>
      </div>

      {/* Легенда + статистика */}
      <div className="mb-2 flex flex-wrap items-center gap-3 text-xs text-[color:var(--stryzh-color-text-secondary)]">
        <LegendDot color="#F7931A" label={`active_order (${stats.active})`} />
        <LegendDot color="#94A3B8" label={`idle (${stats.idle})`} />
        <span>{t('geo.track.distance', { km: stats.distanceKm.toFixed(1) })}</span>
      </div>

      {/* Карта */}
      <div className="rounded border border-white/10 bg-[color:var(--stryzh-color-surface)] p-2">
        {!YANDEX_MAPS_API_KEY ? (
          <Banner tone="muted" text={t('geo.track.noApiKey')} />
        ) : mapLoadError ? (
          <Banner tone="error" text={mapLoadError} />
        ) : (
          <div
            ref={mapContainerRef}
            style={{ width: '100%', height: 480, borderRadius: 6 }}
          />
        )}
        {trackQ.isLoading && (
          <div className="mt-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {t('geo.track.loadingTrack')}
          </div>
        )}
        {trackQ.error ? (
          <div className="mt-2 text-xs text-red-300">
            {t('geo.track.trackError', { message: (trackQ.error as Error).message })}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="rounded border border-white/5 bg-[color:var(--stryzh-color-surface)] p-3">
      <div className="mb-1 text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
        {label}
      </div>
      <div
        className={
          mono
            ? 'font-mono text-sm text-[color:var(--stryzh-color-text-primary)]'
            : 'text-sm text-[color:var(--stryzh-color-text-primary)]'
        }
      >
        {value}
      </div>
    </div>
  )
}

function Banner({ tone, text }: { tone: 'error' | 'muted'; text: string }) {
  const cls =
    tone === 'error'
      ? 'border-red-500/40 bg-red-500/10 text-red-200'
      : 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]'
  return <div className={`rounded border p-4 text-sm ${cls}`}>{text}</div>
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ background: color }}
      />
      {label}
    </span>
  )
}

function haversineKm(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const R = 6371
  const dLat = ((b.lat - a.lat) * Math.PI) / 180
  const dLon = ((b.lon - a.lon) * Math.PI) / 180
  const lat1 = (a.lat * Math.PI) / 180
  const lat2 = (b.lat * Math.PI) / 180
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
  return 2 * R * Math.asin(Math.sqrt(x))
}
